import { useEffect, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { ComponentProps } from '@components/strapi/DynamicZoneComponent';
import { GoogleReviewsSectionDTO } from '@lib/strapi/dtos/components/sections/googleReviewsSection.dto';
import { GoogleReviewsData } from '@lib/frontend/google-reviews';
import { FooterProps } from '@lib/models/layout/footer';
import { IconStar } from '@components/utils/icons';
import { dataOf } from '../shared-ui';
import * as gtag from '@lib/utilities/analytics';

/**
 * Google reviews (D51: server-side Places fetch with ~6h ISR, wired 2026-09-08).
 *
 * With live data: the REAL aggregate (figure + stars + count) and up to
 * `maxItems` review cards, straight from the Business Profile — names,
 * avatars and relative dates as Google shows them, each attributed. Without
 * a key or on any fetch error, the section degrades to the link-only version
 * (D21: we show a real figure or none at all — never an invented one).
 * No structured data on purpose (D38).
 */
/**
 * Clamped review text with a «Περισσότερα» toggle — shown ONLY when the text
 * actually overflows the clamp (measured, not guessed from length), so short
 * reviews never carry a dead control. Expanding is per-card and in place:
 * the reader gets the full text without being sent off-site.
 */
const ReviewText = ({ text }: { text: string }) => {
  const { t } = useTranslation('main');
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (el) setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  return (
    <>
      {/* quotes are the serif's home turf — and 14px was small for five
          clamped lines of reading (owner, 2026-09-09) */}
      <p ref={ref} className={`font-serif text-base leading-relaxed text-body ${expanded ? '' : 'line-clamp-5'}`}>
        {text}
      </p>
      {overflows || expanded ? (
        <button type="button" onClick={() => setExpanded((v) => !v)}
          className="self-start text-sm font-extrabold text-violet-700 hover:underline">
          {expanded ? t('reviews.less') : t('reviews.more')}
        </button>
      ) : null}
    </>
  );
};

const Stars = ({ rating, className = '' }: { rating: number; className?: string }) => (
  <span className={`inline-flex items-center gap-0.5 ${className}`} aria-hidden="true">
    {[1, 2, 3, 4, 5].map((i) => (
      <IconStar key={i} width={16} height={16}
        className={i <= Math.round(rating) ? 'text-arc-yellow' : 'text-rule'} />
    ))}
  </span>
);

const GoogleReviewsSectionImplementation = ({ section, page }: ComponentProps<GoogleReviewsSectionDTO>) => {
  const { t } = useTranslation('main');
  if (!section) return null;
  const live = dataOf(page).googleReviews as GoogleReviewsData | null | undefined;
  const footer = (page as { footer?: FooterProps } | undefined)?.footer;
  const profileUrl = footer?.googleBusinessProfileUrl || live?.profileUrl;
  const reviewUrl = (footer as { googleWriteReviewUrl?: string } | undefined)?.googleWriteReviewUrl || live?.writeReviewUrl;
  if (!live && !profileUrl && !reviewUrl) return null;
  const cards = (live?.reviews ?? []).slice(0, section.maxItems || 5);

  return (
    <section className="my-14 rounded-2xl bg-violet-050 p-8 md:my-20">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold text-ink">
            {section.heading || t('reviews.heading')}
          </h2>
          <p className="mt-2 max-w-xl text-sm text-muted">{t('reviews.note')}</p>
        </div>
        {live && section.showAggregate ? (
          <div className="flex items-center gap-3">
            <span className="text-4xl font-extrabold tabular-nums text-ink">
              {live.rating.toLocaleString('el', { minimumFractionDigits: 1 })}
            </span>
            <span className="flex flex-col">
              <Stars rating={live.rating} />
              <span className="text-base font-bold text-body">
                {t('reviews.countLabel', { count: live.count })}
              </span>
            </span>
          </div>
        ) : null}
      </div>

      {/* li carries min-w-0: grid items default to min-width:auto, so the
          reviewer-name row inflated every card past its track and off
          narrow screens (owner, 2026-09-14); with it the name truncates */}
      {cards.length ? (
        <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((r, i) => (
            <li key={i} className="flex min-w-0 flex-col gap-2.5 rounded-2xl border border-rule bg-white p-5">
              <div className="flex items-center gap-2.5">
                {r.authorPhoto ? (
                  // plain <img>: Google avatar hosts are not in the
                  // next/image allow-list and never need optimisation
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={r.authorPhoto} alt="" width={36} height={36} loading="lazy"
                    referrerPolicy="no-referrer" className="h-9 w-9 flex-none rounded-full" />
                ) : null}
                <div className="min-w-0">
                  {r.authorUrl ? (
                    <a href={r.authorUrl} target="_blank" rel="noreferrer"
                      className="block truncate text-base font-extrabold text-ink hover:text-violet-700">
                      {r.author}
                    </a>
                  ) : (
                    <span className="block truncate text-base font-extrabold text-ink">{r.author}</span>
                  )}
                  <span className="text-sm text-muted">{r.relativeTime}</span>
                </div>
              </div>
              <Stars rating={r.rating} />
              <ReviewText text={r.text} />
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-6 flex flex-wrap gap-3">
        {section.profileLink && profileUrl ? (
          <a href={profileUrl} target="_blank" rel="noreferrer"
            onClick={() => gtag.event('review_click', { event_category: 'engagement', event_label: 'section-profile' })}
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-violet-700 px-5 py-2.5 text-sm font-extrabold text-violet-700 transition-colors hover:bg-violet-700 hover:text-white">
            {t('reviews.viewAll')}
          </a>
        ) : null}
        {section.writeReviewCta && reviewUrl ? (
          <a href={reviewUrl} target="_blank" rel="noreferrer"
            // its own event, not a review_click placement: writing a review
            // is advocacy, seeing reviews is research — the report tables
            // them separately (owner, P11-d)
            onClick={() => gtag.event('review_write_click', { event_category: 'engagement', event_label: 'reviews-section' })}
            className="inline-flex items-center gap-1.5 rounded-full bg-violet-700 px-5 py-2.5 text-sm font-extrabold text-white transition-colors hover:bg-violet-800">
            {t('reviews.write')}
          </a>
        ) : null}
      </div>
    </section>
  );
};

export default GoogleReviewsSectionImplementation;
